import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import Icon from '../icon';
import {
  Animated,
  I18nManager,
  Platform,
  StyleSheet,
  TextInput,
} from 'react-native';
import TextGeneric from '../textGeneric';
import {GenericInputProps} from '../../interfaces/components/textInput';
import Label from '../label';
import {getByScreenSize} from '../../utils/responsive';

const InputContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

const Input = styled(TextInput)<{
  showLeftIcon?: boolean;
  leftIconSize?: number;
}>`
  padding-left: ${({showLeftIcon, leftIconSize}) =>
    showLeftIcon ? getByScreenSize(10, 15) + 20 + leftIconSize : 10}px;
  padding-right: 10px;
  min-height: ${getByScreenSize(50, 55)}px;
  color: ${({theme}) => theme.textInput.text};
  width: 100%;
  font-size: ${({theme}) => theme.text.s7}px;
  font-family: ${({theme}) => {
    if (Platform.OS === 'web') {
      return theme.fonts.en.fontName;
    }
    return theme.fonts.en.regular;
  }};
  ${Platform.OS === 'web' && 'font-weight: 400;'}
`;

const ErrorMessage = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme, hasError, secondColor}) =>
    hasError ? theme.text.error : secondColor || theme.text.grey};
  padding-top: 1%;
`;
const RightIconContainer = styled.View`
  position: absolute;
  right: ${getByScreenSize(10, 15)}px;
  height: 70%;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
`;
const LeftIconContainer = styled.View`
  position: absolute;
  left: ${getByScreenSize(10, 15)}px;
  height: 70%;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
`;

const Container = styled.View``;
const GenericInput: React.FC<GenericInputProps> = ({
  labelStyle,
  secureInput,
  label,
  showRightIcon,
  rightIconName,
  hasError,
  errorMessage,
  rightIconSize,
  rightIconColor,
  onRightIconPress,
  showLeftIcon,
  leftIconName,
  leftIconSize,
  leftIconColor,
  onLeftIconPress,
  enableErrorMessage,
  alwaysShowErrorMessage,
  errorStyle,
  isRequired,
  containerStyle,
  inputContainerStyle,
  scrollEnabled,
  placeholderTextColor,
  placeholderComponent,
  selectionColor,
  value,
  hideFocusedEffect,
  isLabelBold,
  ...props
}): React.ReactElement => {
  /**
   * Represents the current theme.
   * @type {Theme}
   */
  const theme = useTheme() as Theme;
  /**
   * State for controlling the visibility of password input
   */
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);
  /**
   * Ref for animation controller.
   * @type {Animated.Value}
   */
  const animationController = useRef<Animated.Value>(
    new Animated.Value(0),
  ).current;

  useEffect(() => {
    Animated.timing(animationController, {
      duration: 200,
      toValue: hasError ? 1 : 0,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [hasError, animationController]);

  /**
   * Opacity value for error message.
   * @type {Animated.AnimatedInterpolation}
   */
  const opacity = animationController.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });
  /**
   * Renders the right icon component based on the provided props.
   *
   * @returns {React.ReactElement} The rendered right icon component.
   */

  const renderRightIcon = () => {
    if (secureInput) {
      return (
        <RightIconContainer>
          <Icon
            name={passwordVisible ? 'eyeOutline' : 'eyeOff'}
            color={rightIconColor || theme.primary}
            size={theme.text.s55}
            role="button"
            onPress={() => setPasswordVisible(!passwordVisible)}
          />
        </RightIconContainer>
      );
    } else if (showRightIcon && rightIconName) {
      return (
        <RightIconContainer>
          <Icon
            role={onRightIconPress ? 'button' : ''}
            onPress={onRightIconPress}
            name={rightIconName}
            size={rightIconSize || theme.text.s55}
            color={rightIconColor || theme.primary}
          />
        </RightIconContainer>
      );
    }
    return <React.Fragment />;
  };
  const renderLeftIcon = () => {
    return (
      <LeftIconContainer>
        <Icon
          role={onLeftIconPress ? 'button' : ''}
          onPress={onLeftIconPress}
          name={leftIconName || ''}
          size={leftIconSize || theme.text.s55}
          color={
            isFocused && !hideFocusedEffect
              ? theme.secondary
              : leftIconColor || theme.primary
          }
        />
      </LeftIconContainer>
    );
  };
  return (
    <Container style={containerStyle}>
      {label && (
        <Label
          labelStyle={labelStyle}
          isBold={isLabelBold}
          isRequired={isRequired}
          label={label}
        />
      )}
      <InputContainer
        style={[
          {
            backgroundColor: theme.textInput.background,
            borderRadius: 10,
            borderWidth: hideFocusedEffect ? 0 : 1,
          },
          inputContainerStyle,
          {
            borderColor:
              isFocused && !hideFocusedEffect
                ? theme.primary
                : StyleSheet.flatten(inputContainerStyle)?.borderColor ||
                  theme.textInput.border,
          },
        ]}>
        <>
          {showLeftIcon && renderLeftIcon()}
          {!value && !!placeholderComponent ? placeholderComponent() : null}
          <Input
            {...props}
            value={value}
            selectionColor={
              selectionColor || `${theme.textInput.cursorColor}80`
            }
            placeholderTextColor={
              placeholderTextColor || theme.textInput.placeholder
            }
            scrollEnabled={scrollEnabled}
            style={[
              {
                fontFamily: I18nManager.isRTL
                  ? theme.fonts.ar.regular
                  : theme.fonts.en.regular,
                paddingTop: getByScreenSize(15, 17),
                paddingBottom: getByScreenSize(15, 17),
              },
              props.style,
            ]}
            textAlign={I18nManager.isRTL ? 'right' : 'left'}
            secureTextEntry={!passwordVisible && secureInput}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            showLeftIcon={showLeftIcon}
            leftIconSize={leftIconSize || theme.text.s55}
          />
          {renderRightIcon()}
        </>
      </InputContainer>
      {((enableErrorMessage && hasError) || alwaysShowErrorMessage) && (
        <Animated.View
          style={[
            {width: '100%'},
            errorStyle,
            !alwaysShowErrorMessage && {opacity},
          ]}>
          <ErrorMessage hasError={hasError}>{errorMessage}</ErrorMessage>
        </Animated.View>
      )}
    </Container>
  );
};

export default GenericInput;
